import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { TaskAssistantService, TaskQueryRequestDto } from '@pm-ui/pm-services';

interface Message {
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  tokenUsage?: number;
}

@Component({
  selector: 'pm-ui-ai-chat-widget',
  templateUrl: './ai-chat-widget.component.html',
  styleUrls: ['./ai-chat-widget.component.scss']
})
export class AiChatWidgetComponent implements OnInit, AfterViewChecked {
  @ViewChild('scrollMe') private myScrollContainer!: ElementRef;

  isOpen = false;
  userInput = '';
  isLoading = false;
  isMasterAdmin = false;
  messages: Message[] = [
    {
      text: 'Hello! I am your AI Task Assistant. How can I help you with your tasks today?',
      sender: 'ai',
      timestamp: new Date()
    }
  ];

  constructor(private taskAssistantService: TaskAssistantService) { }

  ngOnInit(): void {
    const masterAdmin = localStorage.getItem('isMasterAdmin');
    this.isMasterAdmin = masterAdmin === 'true';
  }

  ngAfterViewChecked() {
    this.scrollToBottom();
  }

  toggleChat() {
    this.isOpen = !this.isOpen;
  }

  seedDatabase() {
    if (this.isLoading) return;
    
    this.isLoading = true;
    this.messages.push({
      text: 'Starting database schema discovery... This may take up to a minute as I process all tables.',
      sender: 'ai',
      timestamp: new Date()
    });

    this.taskAssistantService.seedSchemas().subscribe({
      next: (res: any) => {
        this.isLoading = false;
        this.messages.push({
          text: '✓ Database context successfully seeded. My knowledge base is now up to date!',
          sender: 'ai',
          timestamp: new Date()
        });
      },
      error: (err: any) => {
        this.isLoading = false;
        this.messages.push({
          text: '⚠ Seeding failed. Please check the logs or ensure the Gemini API key is valid.',
          sender: 'ai',
          timestamp: new Date()
        });
        console.error('Seeding Error:', err);
      }
    });
  }

  sendMessage() {
    if (!this.userInput.trim() || this.isLoading) return;

    const userMessage = this.userInput.trim();
    this.messages.push({
      text: userMessage,
      sender: 'user',
      timestamp: new Date()
    });

    this.userInput = '';
    this.isLoading = true;

    // Get current SiteId from localStorage - Key is 'CurrentSiteId' based on workspace storage
    const siteId = Number(localStorage.getItem('CurrentSiteId')) || Number(localStorage.getItem('SiteId')) || 0;

    const request: TaskQueryRequestDto = {
      question: userMessage,
      siteId: siteId
    };

    this.taskAssistantService.query(request).subscribe({
      next: (response: any) => {
        console.log(response);
        this.isLoading = false;
        this.messages.push({
          text: response.answer || 'I processed your request but have no specific summary.',
          sender: 'ai',
          timestamp: new Date(),
          tokenUsage: response.tokenUsage ?? undefined
        });
      },
      error: (err) => {
        this.isLoading = false;
        this.messages.push({
          text: 'Sorry, I encountered an error while processing your request. Please try again later.',
          sender: 'ai',
          timestamp: new Date()
        });
        console.error('AI Assistant Error:', err);
      }
    });
  }

  private scrollToBottom(): void {
    try {
      this.myScrollContainer.nativeElement.scrollTop = this.myScrollContainer.nativeElement.scrollHeight;
    } catch (err) { }
  }
}
